import React from "react"
import { ArrowSubmitButton, ReimbursementDialogFraming, BackArrow } from "../shared/layouts"
import { Progress } from "#/components/views/modal/Progress"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { useReimbursementContext } from "#/components/context/reimbursement/GetReimbursedContext"
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from "react-intl"
import { Box, DialogContentText, DialogTitle } from "@carrotfertility/carotene-core"
import { DialogActionsBar, Form, FormOnSubmitHandler, FormRadioGroup, useWatch } from "@carrotfertility/carotene-core-x"
import { isPelvicFloorSubtype, JourneySubTypes } from "./ExpenseJourneyTypes"
import { PelvicFloorRequirementsCard } from "./modules/PelvicFloorRequirementsCard"

const PFPT_NO = "PFPT_NO"

type PfptOption = { label: MessageDescriptor; value: string }

const pfptOptions: PfptOption[] = [
  { value: PFPT_NO, label: defineMessage({ defaultMessage: "No" }) },
  {
    value: JourneySubTypes.PELVIC_FLOOR_THERAPY_VIRTUAL,
    label: defineMessage({ defaultMessage: "Yes: Virtual care" })
  },
  {
    value: JourneySubTypes.PELVIC_FLOOR_THERAPY_IN_PERSON,
    label: defineMessage({ defaultMessage: "Yes: In-person care" })
  }
]

const PelvicFloorModule = (): JSX.Element | null => {
  const selected = useWatch({ name: "relatedToPelvicFloorTherapy" })
  return isPelvicFloorSubtype(selected) ? <PelvicFloorRequirementsCard /> : null
}

export const RelatedToPelvicFloorPhysicalTherapy = (): JSX.Element => {
  const { send, back } = useStateMachine()
  const { updateJourneySubtype, updateRelatedToPelvicFloorTherapy, state } = useReimbursementContext()
  const intl = useIntl()

  const formattedOptions = pfptOptions.map((o) => ({ ...o, label: intl.formatMessage(o.label) }))

  const handleOnSubmit: FormOnSubmitHandler<{ relatedToPelvicFloorTherapy: string }> = (values) => {
    const answer = values.relatedToPelvicFloorTherapy
    const selected = formattedOptions.find((o) => o.value === answer)
    updateRelatedToPelvicFloorTherapy(selected)

    if (answer === PFPT_NO) {
      send("")
      return
    }
    updateJourneySubtype(selected)
    send("ADDITIONAL_INFO_ANYTHING_ELSE")
  }

  return (
    <Form
      onSubmit={handleOnSubmit}
      defaultValues={{ relatedToPelvicFloorTherapy: state.relatedToPelvicFloorTherapy?.value || "" }}
    >
      <Progress
        numSteps={3}
        step={2}
        label={intl.formatMessage({ defaultMessage: "About This Expense" })}
        inactiveStepBackgroundColor="#DBE6EE"
      />
      <BackArrow onClick={back} />
      <ReimbursementDialogFraming>
        <DialogTitle id="step-heading">
          <FormattedMessage defaultMessage="Is this related to pelvic floor physical therapy?" />
        </DialogTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: (theme) => theme.spacing(theme.tokens.spacing.lg) }}>
          <DialogContentText>
            <FormattedMessage defaultMessage="If you're not sure, choose the option that seems like the best fit." />
          </DialogContentText>
          <FormRadioGroup
            aria-labelledby="step-heading"
            name="relatedToPelvicFloorTherapy"
            controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
            options={formattedOptions}
          />
          <PelvicFloorModule />
        </Box>
      </ReimbursementDialogFraming>
      <DialogActionsBar
        primaryAction={
          <ArrowSubmitButton>
            <FormattedMessage defaultMessage="Continue" />
          </ArrowSubmitButton>
        }
      />
    </Form>
  )
}
